import React, { useEffect, useMemo, useState } from 'react';
import InvoicePreview from '@/components/molecules/invoice/InvoicePreview';
import { Invoice } from '@/services/types/invoice.types';
import { invoiceService } from '@/services/invoice.service';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setBreadcrumbs } from '@/store/slices/breadcrumb';
import { PAGE } from '@/routes';
import { Budget, BudgetMemberEntry } from '@/services/types/budgets.types';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ReactComponent as EditIcon } from '@/assets/icons/edit-purple.svg';
import { ReactComponent as DeleteIcon } from '@/assets/icons/bin.svg';
import InvoiceInfo from '@/components/organisms/invoice/InvoiceInfo';
import { InvoiceCategory, PaymentType, InvoiceStatus } from '@/services/types/enums/invoice.enum';
import { CircleXIcon, Ban } from 'lucide-react';
import { ReactComponent as CompleteIcon } from '@/assets/icons/completed.svg';
import InvoiceAllocationsDetails, { AllocationData } from '@/components/organisms/invoice/InvoiceAllocationsDetails';
import { accountingCenterService } from '@/services/accounting-centers.service';
import { bankAccountService } from '@/services/bank-accounts.service';
import { expenseRevenueService } from '@/services/expense-category.service';
import { revenueCategoryService } from '@/services/revenue-category.service';
import InfoModal from '@/components/molecules/InfoModal';
import toast from 'react-hot-toast';
import { ReactComponent as DeleteInvoiceIcon } from '@/assets/icons/delete-invoice.svg';
import { ReactComponent as ValidateInvoiceIcon } from '@/assets/icons/validate-invoice.svg';
import { calculateRemainingDays } from '@/utils/helpers';
import MobileAppBar from '@/components/molecules/MobileAppBar';
import ValidateConfirmModal from '@/components/molecules/invoice/ValidateConfirmModal';
import WorkActivitySubmitterProfile from '@/components/molecules/workActivity/WorkActivitySubmitterProfile';
import { budgetService } from '@/services/budget.service';
import { useBudgetPermissions } from '@/hooks/useBudgetPermissions';

// Helper function to convert hex to HSL
const hexToHSL = (hex: string): { h: number; s: number; l: number } => {
  const r = parseInt(hex.substring(1, 3), 16) / 255;
  const g = parseInt(hex.substring(3, 5), 16) / 255;
  const b = parseInt(hex.substring(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

// Helper function to convert HSL to hex
const hslToHex = (h: number, s: number, l: number): string => {
  s /= 100;
  l /= 100;

  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

  const r = Math.round(255 * f(0));
  const g = Math.round(255 * f(8));
  const b = Math.round(255 * f(4));

  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
};

// Helper function to generate colors for items
const generateColorForIndex = (index: number): string => {
  const baseColors = ['#7663EB', '#48C80C'];

  // Return predefined colors for first two items
  if (index < baseColors.length) {
    return baseColors[index];
  }

  // For 3rd and onward items, generate random colors with similar brightness
  const hsl1 = hexToHSL(baseColors[0]);
  const hsl2 = hexToHSL(baseColors[1]);

  // Calculate average lightness and saturation
  const avgLightness = (hsl1.l + hsl2.l) / 2;
  const avgSaturation = (hsl1.s + hsl2.s) / 2;

  // Generate random hue but keep similar lightness and saturation with some variation
  const randomHue = Math.floor(Math.random() * 360);
  const lightnessVariation = (Math.random() - 0.5) * 15; // ±7.5% variation
  const saturationVariation = (Math.random() - 0.5) * 20; // ±10% variation

  const newLightness = Math.max(30, Math.min(70, avgLightness + lightnessVariation));
  const newSaturation = Math.max(40, Math.min(100, avgSaturation + saturationVariation));

  return hslToHex(randomHue, newSaturation, newLightness);
};

const InvoiceDetailPage = () => {
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('information');
  const [allocationData, setAllocationData] = useState<{
    bankAccounts: AllocationData[];
    departments: AllocationData[];
    projects: AllocationData[];
    contracts: AllocationData[];
    productsAndServices: AllocationData[];
    expenseCategories: AllocationData[];
    revenueCategories: AllocationData[];
  }>({
    bankAccounts: [],
    departments: [],
    projects: [],
    contracts: [],
    productsAndServices: [],
    expenseCategories: [],
    revenueCategories: []
  });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [loadingDeleteInvoice, setLoadingDeleteInvoice] = useState(false);
  const [validateConfirmModal, setValidateConfirmModal] = useState(false);
  const [loadingValidateInvoice, setLoadingValidateInvoice] = useState(false);
  const [submitterProfile, setSubmitterProfile] = useState<{ name: string; profilePicUrl: string | null } | null>(null);
  const [budgetMembers, setBudgetMembers] = useState<BudgetMemberEntry[]>([]);

  const { invoiceId, budgetId } = useParams<{ invoiceId?: string; budgetId?: string }>();
  const location = useLocation();
  const budget = location.state?.budget as Budget | undefined;
  const fromAccountingCenter = location.state?.from === 'accountingCenter';
  const acType = location.state?.acType as string | undefined;
  const acCenter = location.state?.acCenter as { name?: string; _id?: string } | undefined;
  const fromExpenseCategory = location.state?.from === 'expenseCategory';
  const ecData = location.state?.ecData as { name?: string; _id?: string } | undefined;
  const fromRevenueCategory = location.state?.from === 'revenueCategory';
  const rcData = location.state?.rcData as { name?: string; _id?: string } | undefined;
  const fromBankAccount = location.state?.from === 'bankAccount';
  const bankAccount = location.state?.bankAccount as { accountName?: string; name?: string; _id?: string } | undefined;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { policies } = useBudgetPermissions({
    budgetId: invoice?.budget_id ?? budgetId,
    members: budgetMembers.length > 0 ? budgetMembers : undefined,
    fallbackMembers: budget?.members ?? []
  });

  const canModify = useMemo(() => policies.canModifyInvoice(invoice), [policies, invoice]);
  const canDelete = useMemo(() => policies.canDeleteInvoice(invoice), [policies, invoice]);
  const canValidateInvoice = useMemo(() => policies.canValidateInvoice(invoice), [policies, invoice]);

  const getAcTypeLabel = (type: string | undefined) => {
    if (!type) return 'Accounting Centers';
    return (
      type
        .split('_')
        .map((s) => s.charAt(0) + s.slice(1).toLowerCase())
        .join(' ') + ' Accounting Centers'
    );
  };

  console.log('budget asd', budget);

  const fetchAllocationData = async () => {
    if (!invoice || !invoice.budget_id) return;

    try {
      const budgetId = invoice.budget_id!;
      // Fetch all necessary data
      const [accountingCentersRes, bankAccountsRes, expenseCategoriesRes, revenueCategoriesRes] = await Promise.all([
        accountingCenterService.getAccountingCenters(budgetId),
        bankAccountService.getBankAccounts(budgetId),
        expenseRevenueService.getExpenseCategories(budgetId),
        revenueCategoryService.getRevenueCategories(budgetId)
      ]);

      // Map bank accounts
      const bankAccountsData: AllocationData[] = [];
      if (invoice.allocations?.allocation_bank_account) {
        invoice.allocations.allocation_bank_account.forEach((allocation, index) => {
          const bankAccount = bankAccountsRes.data.find((ba: any) => ba._id === allocation.id);
          if (bankAccount) {
            bankAccountsData.push({
              id: allocation.id,
              name: bankAccount.accountName || 'Unknown',
              value: allocation.value,
              color: generateColorForIndex(index)
            });
          }
        });
      }

      // Map department accounting centers
      const departmentsData: AllocationData[] = [];
      if (invoice.allocations?.allocation_department_ac) {
        invoice.allocations.allocation_department_ac.forEach((allocation, index) => {
          const center = accountingCentersRes.data.find((ac: any) => ac._id === allocation.id);
          if (center) {
            departmentsData.push({
              id: allocation.id,
              name: center.name || 'Unknown',
              value: allocation.value,
              color: generateColorForIndex(index)
            });
          }
        });
      }

      // Map project accounting centers
      const projectsData: AllocationData[] = [];
      if (invoice.allocations?.allocation_project_ac) {
        invoice.allocations.allocation_project_ac.forEach((allocation, index) => {
          const center = accountingCentersRes.data.find((ac: any) => ac._id === allocation.id);
          if (center) {
            projectsData.push({
              id: allocation.id,
              name: center.name || 'Unknown',
              value: allocation.value,
              color: generateColorForIndex(index)
            });
          }
        });
      }

      // Map contract accounting centers
      const contractsData: AllocationData[] = [];
      if (invoice.allocations?.allocation_contract_ac) {
        invoice.allocations.allocation_contract_ac.forEach((allocation, index) => {
          const center = accountingCentersRes.data.find((ac: any) => ac._id === allocation.id);
          if (center) {
            contractsData.push({
              id: allocation.id,
              name: center.name || 'Unknown',
              value: allocation.value,
              color: generateColorForIndex(index)
            });
          }
        });
      }

      // Map products and services accounting centers
      const productsAndServicesData: AllocationData[] = [];
      if (invoice.allocations?.allocation_productAndServices_ac) {
        invoice.allocations.allocation_productAndServices_ac.forEach((allocation, index) => {
          const center = accountingCentersRes.data.find((ac: any) => ac._id === allocation.id);
          if (center) {
            productsAndServicesData.push({
              id: allocation.id,
              name: center.name || 'Unknown',
              value: allocation.value,
              color: generateColorForIndex(index)
            });
          }
        });
      }

      // Map expense categories
      const expenseCategoriesData: AllocationData[] = [];
      if (invoice.allocations?.allocation_expense_category) {
        invoice.allocations.allocation_expense_category.forEach((allocation, index) => {
          const category = expenseCategoriesRes.data.find((ec: any) => ec._id === allocation.id);
          if (category) {
            expenseCategoriesData.push({
              id: allocation.id,
              name: category.name || 'Unknown',
              value: allocation.value,
              color: generateColorForIndex(index)
            });
          }
        });
      }

      // Map revenue categories
      const revenueCategoriesData: AllocationData[] = [];
      if (invoice.allocations?.allocation_revenue_category) {
        invoice.allocations.allocation_revenue_category.forEach((allocation, index) => {
          const category = revenueCategoriesRes.data.find((rc: any) => rc._id === allocation.id);
          if (category) {
            revenueCategoriesData.push({
              id: allocation.id,
              name: category.name || 'Unknown',
              value: allocation.value,
              color: generateColorForIndex(index)
            });
          }
        });
      }

      setAllocationData({
        bankAccounts: bankAccountsData,
        departments: departmentsData,
        projects: projectsData,
        contracts: contractsData,
        productsAndServices: productsAndServicesData,
        expenseCategories: expenseCategoriesData,
        revenueCategories: revenueCategoriesData
      });
    } catch (error) {
      console.error('Error fetching allocation data:', error);
    }
  };

  useEffect(() => {
    if (budgetId && fromExpenseCategory) {
      const crumbs: { title: string; path: string }[] = [
        { title: 'Budgets', path: PAGE.BUDGETS.path },
        { title: budget?.name || 'Budget Overview', path: `${PAGE.BUDGETS.path}/${budgetId}` },
        { title: 'Expense Categories', path: `${PAGE.BUDGETS.path}/${budgetId}/expense-categories` }
      ];
      if (ecData?.name) {
        crumbs.push({
          title: ecData.name,
          path: `${PAGE.BUDGETS.path}/${budgetId}/expense-categories/${ecData._id || ''}`
        });
      }
      crumbs.push(
        { title: 'Invoices', path: `${PAGE.BUDGETS.path}/${budgetId}/invoices` },
        { title: 'Invoice Detail', path: '' }
      );
      dispatch(setBreadcrumbs(crumbs));
    } else if (budgetId && fromRevenueCategory) {
      const crumbs: { title: string; path: string }[] = [
        { title: 'Budgets', path: PAGE.BUDGETS.path },
        { title: budget?.name || 'Budget Overview', path: `${PAGE.BUDGETS.path}/${budgetId}` },
        { title: 'Revenue Categories', path: `${PAGE.BUDGETS.path}/${budgetId}/revenue-categories` }
      ];
      if (rcData?.name) {
        crumbs.push({
          title: rcData.name,
          path: `${PAGE.BUDGETS.path}/${budgetId}/revenue-categories/${rcData._id || ''}`
        });
      }
      crumbs.push(
        { title: 'Invoices', path: `${PAGE.BUDGETS.path}/${budgetId}/invoices` },
        { title: 'Invoice Detail', path: '' }
      );
      dispatch(setBreadcrumbs(crumbs));
    } else if (budgetId && fromBankAccount) {
      const crumbs: { title: string; path: string }[] = [
        { title: 'Budgets', path: PAGE.BUDGETS.path },
        { title: budget?.name || 'Budget Overview', path: `${PAGE.BUDGETS.path}/${budgetId}` },
        { title: 'Bank Accounts', path: `${PAGE.BUDGETS.path}/${budgetId}/bank-accounts` }
      ];
      if (bankAccount && (bankAccount.accountName || bankAccount.name)) {
        crumbs.push({
          title: bankAccount.accountName || bankAccount.name || 'Bank Account',
          path: `${PAGE.BUDGETS.path}/${budgetId}/bank-accounts/${bankAccount._id || ''}`
        });
      }
      crumbs.push(
        { title: 'Invoices', path: `${PAGE.BUDGETS.path}/${budgetId}/invoices` },
        { title: 'Invoice Detail', path: '' }
      );
      dispatch(setBreadcrumbs(crumbs));
    } else if (budgetId && fromAccountingCenter && acType) {
      // From Accounting Center context: Budgets -> Budget Overview -> [AC Type] -> [AC name] -> Invoices -> Invoice Detail
      const crumbs: { title: string; path: string }[] = [
        { title: 'Budgets', path: PAGE.BUDGETS.path },
        { title: budget?.name || 'Budget Overview', path: `${PAGE.BUDGETS.path}/${budgetId}` },
        {
          title: getAcTypeLabel(acType),
          path: `${PAGE.BUDGETS.path}/${budgetId}/accounting-centers/${acType}`
        }
      ];
      if (acCenter?.name) {
        crumbs.push({
          title: acCenter.name,
          path: `${PAGE.BUDGETS.path}/${budgetId}/accounting-centers/${acType}/${acCenter._id || ''}`
        });
      }
      crumbs.push(
        { title: 'Invoices', path: `${PAGE.BUDGETS.path}/${budgetId}/invoices` },
        { title: 'Invoice Detail', path: '' }
      );
      dispatch(setBreadcrumbs(crumbs));
    } else if (budgetId) {
      // From Budget context: Budgets -> Budget Overview -> Invoices -> Invoice Detail
      dispatch(
        setBreadcrumbs([
          { title: 'Budgets', path: PAGE.BUDGETS.path },
          { title: budget?.name || 'Budget Overview', path: `${PAGE.BUDGETS.path}/${budgetId}` },
          { title: 'Invoices', path: `${PAGE.BUDGETS.path}/${budgetId}/invoices` },
          { title: 'Invoice Detail', path: '' }
        ])
      );
    } else {
      // From Invoices context: Invoices -> Invoice Detail
      dispatch(
        setBreadcrumbs([
          { title: 'Invoices', path: PAGE.INVOICE.path },
          { title: 'Invoice Detail', path: '' }
        ])
      );
    }
  }, [
    dispatch,
    budgetId,
    budget?.name,
    fromAccountingCenter,
    acType,
    acCenter?.name,
    acCenter?._id,
    fromExpenseCategory,
    ecData?.name,
    ecData?._id,
    fromRevenueCategory,
    rcData?.name,
    rcData?._id,
    fromBankAccount,
    bankAccount?.accountName,
    bankAccount?.name,
    bankAccount?._id
  ]);

  useEffect(() => {
    setLoading(true);
    invoiceService
      .getInvoice(invoiceId || '')
      .then((res) => {
        console.log('res', res);
        setInvoice(res);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [invoiceId]);

  // Fetch allocation data once when invoice is loaded
  useEffect(() => {
    if (invoice) {
      fetchAllocationData();
    }
  }, [invoice]);

  useEffect(() => {
    if (!invoice) {
      setSubmitterProfile(null);
      return;
    }

    if (invoice.creator?.name) {
      setSubmitterProfile({
        name: invoice.creator.name,
        profilePicUrl: invoice.creator.profile_pic_url
      });
    }

    const resolvedBudgetId = invoice.budget_id || budgetId;
    if (!resolvedBudgetId) {
      if (!invoice.creator?.name) {
        setSubmitterProfile(null);
      }
      setBudgetMembers([]);
      return;
    }

    if (budget?.members?.length) {
      setBudgetMembers(budget.members);
      if (!invoice.creator?.name && invoice.user_id) {
        const member = budget.members.find((entry) => entry.user_id === invoice.user_id);
        setSubmitterProfile(member ? { name: member.name, profilePicUrl: member.profile_pic_url } : null);
      }
      return;
    }

    budgetService
      .getBudget(resolvedBudgetId)
      .then(({ data }) => {
        setBudgetMembers(data.members ?? []);
        if (!invoice.creator?.name && invoice.user_id) {
          const member = data.members?.find((entry) => entry.user_id === invoice.user_id);
          setSubmitterProfile(member ? { name: member.name, profilePicUrl: member.profile_pic_url } : null);
        }
      })
      .catch((error) => {
        console.error('Error fetching budget members for invoice permissions:', error);
        if (!invoice.creator?.name) {
          setSubmitterProfile(null);
        }
        setBudgetMembers([]);
      });
  }, [invoice, budgetId, budget?.members]);

  const handleDeleteInvoice = async () => {
    setLoadingDeleteInvoice(true);
    try {
      if (invoice?.is_draft) {
        await invoiceService.deleteDraftInvoice(invoiceId || '');
      } else {
        await invoiceService.deleteInvoice(invoiceId || '');
      }
      toast.success('Invoice deleted successfully');
      navigate(-1);
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Failed to delete invoice');
      setLoadingDeleteInvoice(false);
    } finally {
      setLoadingDeleteInvoice(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full flex-col gap-2">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="text-sm font-medium">Loading invoice details...</span>
      </div>
    );
  }

  console.log('allocationData', allocationData);

  const invoiceInfoItems: {
    label: string;
    value: string | Date | string[] | React.ReactNode | undefined;
    tooltipText?: string;
    type: 'image' | 'date' | 'text';
    span: number;
  }[] = [
    {
      label: 'Image',
      value: invoice?.invoice_images_url,
      type: 'image',
      span: 2
    },
    {
      label: 'Invoice name',
      value: invoice?.name,
      type: 'text',
      span: 1
    },
    {
      label: 'Budget',
      value: invoice?.budget_name,
      type: 'text',
      span: 1
    },
    {
      label: 'Invoicing',
      value: invoice?.invoicing_company_name,
      type: 'text',
      span: 1
    },
    {
      label: 'Invoiced',
      value: invoice?.invoiced_company_name,
      type: 'text',
      span: 1
    },
    {
      label: 'Type',
      value: invoice?.invoice_category === InvoiceCategory.EXPENSE ? 'Expense' : 'Revenue',
      type: 'text',
      span: 1
    },
    {
      label: 'Status',
      value: (
        <div className="flex items-center gap-1">
          <span>
            {invoice?.status === InvoiceStatus.PAID ? (
              <CompleteIcon />
            ) : invoice?.status === InvoiceStatus.CANCELLED ? (
              <Ban className="w-[16px] h-[16px]" />
            ) : (
              <CircleXIcon className="w-[16px] h-[16px]" />
            )}{' '}
          </span>
          <span>
            {invoice?.status === InvoiceStatus.PAID
              ? 'Paid'
              : invoice?.status === InvoiceStatus.CANCELLED
                ? 'Cancelled'
                : 'Not Paid'}
          </span>
        </div>
      ),
      type: 'text',
      span: 1
    },
    {
      label: 'Currency',
      value: invoice?.payment_currency === 'EUR' ? '€ Euro' : '$ Dollar',
      type: 'text',
      span: 1
    },
    {
      label: 'Amount',
      value: invoice?.payment_amount
        ? invoice?.payment_currency === 'EUR'
          ? `€ ${invoice?.payment_amount}`
          : `$ ${invoice?.payment_amount}`
        : undefined,
      type: 'text',
      span: 1
    },
    {
      label: 'Issue date',
      value: invoice?.payment_issue_date,
      tooltipText: 'The date the invoice was issued.',
      type: 'date',
      span: 1
    },
    {
      label: 'Payment term',
      value: invoice?.payment_term_date,
      tooltipText: 'The date the invoice was due.',
      type: 'date',
      span: 1
    },
    {
      label: 'Payment date',
      value: invoice?.payment_date,
      tooltipText: 'The date the invoice was paid.',
      type: 'date',
      span: 1
    },
    {
      label: 'Payment gap',
      value:
        invoice?.payment_date && invoice?.payment_term_date
          ? `${Math.round(
              (new Date(invoice.payment_date).getTime() - new Date(invoice?.payment_term_date).getTime()) /
                (1000 * 60 * 60 * 24)
            )} days`
          : undefined,
      tooltipText: 'Number of days between issue date and payment date.',
      type: 'text',
      span: 1
    },
    {
      label: 'Invoicing contact',
      value: invoice?.invoicing_contact,
      type: 'text',
      span: 1
    }
  ];

  const remainingDays = calculateRemainingDays(invoice?.createdAt || '');

  const handleValidateInvoice = async (paymentDate?: Date | null) => {
    setLoadingValidateInvoice(true);
    try {
      await invoiceService.validateInvoice(invoiceId || '', paymentDate);
      toast.success('Invoice validated successfully');
      navigate(-1);
    } catch (error) {
      console.error('Error validating invoice:', error);
      toast.error('Failed to validate invoice');
      setLoadingValidateInvoice(false);
    } finally {
      setLoadingValidateInvoice(false);
    }
  };

  const handleEditInvoice = () => {
    if (invoice?.is_draft) {
      navigate(`${PAGE.INVOICE.path}/${PAGE.INVOICE.children.CREATE}`, {
        state: { from: 'draft', invoiceId: invoice?._id }
      });
    } else {
      navigate(`${PAGE.INVOICE.path}/${PAGE.INVOICE.children.EDIT}`, {
        state: { from: 'invoiceDetail', invoiceId: invoice?._id }
      });
    }
  };

  return (
    <>
      <MobileAppBar
        title={<span className="capitalize">Invoice Detail</span>}
        onRightIconClick={canModify && !invoice?.is_validated ? handleEditInvoice : undefined}
        rightIcon={
          canModify && !invoice?.is_validated ? <EditIcon className="w-[18px] h-[18px] text-dark" /> : undefined
        }
        rightTwoIcon={canDelete && !invoice?.is_validated ? <DeleteIcon className="w-5 h-5" /> : undefined}
        onRightTwoIconClick={canDelete && !invoice?.is_validated ? () => setDeleteConfirmModal(true) : undefined}
      />
      <div
        className={`flex-1 p-4 md:p-0 w-full max-w-[1440px] mx-auto flex flex-col md:flex-row gap-6 justify-between ${
          canValidateInvoice ? 'pb-32 md:pb-0' : ''
        }`}
      >
        <aside className="md:w-[330px] pt-12 space-y-6 flex flex-col">
          <InvoicePreview invoice={{ ...(invoice as Invoice & { budget?: Budget }), budget: budget || undefined }} />

          {submitterProfile && (
            <WorkActivitySubmitterProfile name={submitterProfile.name} profilePicUrl={submitterProfile.profilePicUrl} />
          )}

          {canValidateInvoice && (
            <div className="fixed bottom-0 left-0 right-0 z-10 bg-white p-4 shadow-[0_-2px_8px_rgba(0,0,0,0.08)] md:static md:z-auto md:flex-1 md:flex md:flex-col md:justify-end md:items-end md:bg-transparent md:p-0 md:shadow-none">
              <Button
                variant="default"
                className="rounded-full flex items-center justify-center gap-2 w-full text-sm md:text-base font-semibold mt-auto mb-0"
                onClick={() => setValidateConfirmModal(true)}
              >
                <span>Validate</span>
              </Button>
              <p className="text-secondary text-xs md:text-sm font-medium text-center mt-2">
                After you validate the invoice, it will be finalized and unable to be edited anymore.
              </p>
            </div>
          )}
        </aside>
        <div className="p-2 flex-1 flex flex-col gap-4">
          {/* Tabs */}
          <div className="flex border-b items-center gap-4">
            <button
              className={`text-sm md:text-lg font-normal px-0 md:px-0 py-1 md:py-2 border-b-2 ${activeTab === 'information' ? 'border-primary font-medium' : 'border-transparent'} `}
              onClick={() => setActiveTab('information')}
            >
              Information
            </button>
            <button
              className={`text-sm md:text-lg font-normal px-0 md:px-0 md:py-2 py-1 border-b-2 ${activeTab === 'allocation' ? 'border-primary font-medium' : 'border-transparent'} `}
              onClick={() => setActiveTab('allocation')}
            >
              Allocation
            </button>
            <button
              className={`text-sm md:text-lg font-normal px-0 md:px-0 md:py-2 py-1 border-b-2 ${activeTab === 'tag' ? 'border-primary font-medium' : 'border-transparent'} `}
              onClick={() => setActiveTab('tag')}
            >
              Tag
            </button>
          </div>

          <div className="flex-1 flex flex-col md:border md:bg-white md:p-4 md:rounded-[16px]">
            <div className="flex-1">
              {activeTab === 'information' && (
                <InvoiceInfo items={invoiceInfoItems} remainingDays={invoice?.is_draft ? remainingDays : undefined} />
              )}
              {activeTab === 'allocation' && (
                <InvoiceAllocationsDetails
                  invoice={invoice}
                  bankAccounts={allocationData.bankAccounts}
                  departments={allocationData.departments}
                  projects={allocationData.projects}
                  contracts={allocationData.contracts}
                  productsAndServices={allocationData.productsAndServices}
                  expenseCategories={allocationData.expenseCategories}
                  revenueCategories={allocationData.revenueCategories}
                />
              )}
              {activeTab === 'tag' && (
                <div>
                  <p className="text-sm font-medium mb-2">Invoice Tag</p>
                  <div className="flex flex-wrap gap-2">
                    {invoice && invoice?.tags && invoice?.tags.length > 0 ? (
                      invoice?.tags?.map((tag) => (
                        <span key={tag} className="text-sm font-medium border border-[#E9ECEF] rounded-[8px] py-1 px-3">
                          {tag}
                        </span>
                      ))
                    ) : (
                      <p className="text-sm font-medium text-secondary">No tags found for this invoice</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <footer>
              {(canDelete || canModify) && (
                <div className="hidden md:flex items-center justify-end gap-2">
                  {canDelete && (
                    <Button
                      variant="text"
                      className="text-[#E8507E] flex items-center justify-center gap-2 w-fit text-base font-semibold mt-auto mb-0"
                      onClick={() => setDeleteConfirmModal(true)}
                    >
                      <DeleteIcon className="w-5 h-5" />
                      <span>Delete {invoice?.is_draft ? 'Draft Invoice' : 'Invoice'}</span>
                    </Button>
                  )}
                  {canModify && !invoice?.is_validated && (
                    <Button
                      variant="primary-reverse"
                      className="flex items-center justify-center gap-2 w-fit rounded-full text-base font-semibold mt-auto mb-0"
                      onClick={handleEditInvoice}
                    >
                      <EditIcon className="w-5 h-5" />
                      <span>{invoice?.is_draft ? 'Create Invoice' : 'Edit Invoice'}</span>
                    </Button>
                  )}
                </div>
              )}
            </footer>
          </div>
        </div>

        {/* Delete Confirm Modal */}
        {deleteConfirmModal && (
          <InfoModal
            isOpen={deleteConfirmModal}
            variant="confirm"
            title="Are you sure want to Delete this Invoice?"
            message={<p>Deleted Invoice will be removed permanently and unable to restore</p>}
            buttonText="Delete"
            onButtonClick={handleDeleteInvoice}
            onClose={() => setDeleteConfirmModal(false)}
            icon={<DeleteInvoiceIcon />}
            loading={loadingDeleteInvoice}
            loadingButtonText="Deleting..."
          />
        )}

        {/* Validate Confirm Modal */}
        {validateConfirmModal && (
          <ValidateConfirmModal
            isOpen={validateConfirmModal}
            variant="success"
            title="Are you sure want to validate this invoice?"
            message={<p>Once validated, you will be unable to edit it anymore.</p>}
            buttonText="Validate"
            onConfirm={handleValidateInvoice}
            onClose={() => setValidateConfirmModal(false)}
            icon={<ValidateInvoiceIcon />}
            loading={loadingValidateInvoice}
            hasPaymentDate={!!invoice?.payment_date}
            paymentIssueDate={invoice?.payment_issue_date}
          />
        )}
      </div>
    </>
  );
};

export default InvoiceDetailPage;
